import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { getEvent } from "../firebase/firestoreService";
import { calcTicketTotal } from "../services/ticketing";
import "../styles/events.css";

const fmtDate = (ts) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

export default function EventDetail({ cartCount, onCartClick }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event,   setEvent]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [qtyById, setQtyById] = useState({});
  const [copied,  setCopied]  = useState(false);

  const shareLink = () => {
    const url = `${window.location.origin}/events/${eventId}`;
    if (navigator.share) {
      navigator.share({ title: event?.name, url }).catch(() => {});
      return;
    }
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    getEvent(eventId).then((e) => { setEvent(e); setLoading(false); });
  }, [eventId]);

  if (loading) {
    return (
      <div className="events-page">
        <Navbar cartCount={cartCount || 0} onCartClick={onCartClick || (() => {})} />
        <div className="events-page__loading"><div className="events-page__spinner" /><p>Loading…</p></div>
      </div>
    );
  }

  if (!event || event.status !== "active") {
    return (
      <div className="events-page">
        <Navbar cartCount={cartCount || 0} onCartClick={onCartClick || (() => {})} />
        <p className="events-page__empty">This event isn't available.</p>
      </div>
    );
  }

  const setQty = (ticketTypeId, delta, remaining) => {
    setQtyById((prev) => {
      const next = Math.max(0, Math.min(remaining, (prev[ticketTypeId] || 0) + delta));
      return { ...prev, [ticketTypeId]: next };
    });
  };

  const selections = (event.ticketTypes || [])
    .map((tt) => ({ ticketTypeId: tt.id, name: tt.name, price: tt.price, qty: qtyById[tt.id] || 0 }))
    .filter((s) => s.qty > 0);

  const totalQty = selections.reduce((s, x) => s + x.qty, 0);
  const subtotal = selections.reduce((s, x) => s + x.qty * x.price, 0);
  const { total: totalPrice } = calcTicketTotal(subtotal);

  const handleContinue = () => {
    if (!totalQty) return;
    navigate(`/events/${event.id}/checkout`, { state: { selections } });
  };

  return (
    <div className="events-page">
      <Navbar cartCount={cartCount || 0} onCartClick={onCartClick || (() => {})} />

      <main className="event-detail__main">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <button className="event-detail__back" onClick={() => navigate("/events")}>← Back to Events</button>
          <button className="event-detail__back" onClick={shareLink}>
            {copied ? "Link copied!" : "🔗 Share"}
          </button>
        </div>

        {event.image && <img className="event-detail__img" src={event.image} alt={event.name} />}

        <h1 className="event-detail__name">{event.name}</h1>
        <p className="event-detail__meta">{fmtDate(event.eventDate)}</p>
        {(event.venue || event.address) && (
          <p className="event-detail__venue">📍 {[event.venue, event.address].filter(Boolean).join(" · ")}</p>
        )}

        {event.description && <p className="event-detail__desc">{event.description}</p>}

        <div className="event-detail__ticket-types">
          {(event.ticketTypes || []).map((tt) => {
            const remaining = Math.max(0, (tt.capacity || 0) - (tt.sold || 0));
            const qty = qtyById[tt.id] || 0;
            return (
              <div key={tt.id} className="ticket-type-row">
                <div className="ticket-type-row__info">
                  <p className="ticket-type-row__name">{tt.name}</p>
                  {remaining > 0 ? (
                    <p className="ticket-type-row__price">MK {tt.price.toLocaleString()}</p>
                  ) : (
                    <p className="ticket-type-row__sold-out">SOLD OUT</p>
                  )}
                </div>
                {remaining > 0 && (
                  <div className="ticket-type-row__stepper">
                    <button
                      className="ticket-type-row__stepper-btn"
                      onClick={() => setQty(tt.id, -1, remaining)}
                      disabled={qty === 0}
                    >−</button>
                    <span className="ticket-type-row__qty">{qty}</span>
                    <button
                      className="ticket-type-row__stepper-btn"
                      onClick={() => setQty(tt.id, 1, remaining)}
                      disabled={qty >= remaining}
                    >+</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      <div className="event-detail__bar">
        <div className="event-detail__bar-total">
          <span>{totalQty} ticket{totalQty !== 1 ? "s" : ""} · incl. service fee</span>
          MK {totalPrice.toLocaleString()}
        </div>
        <button className="event-detail__bar-btn" onClick={handleContinue} disabled={!totalQty}>
          Continue
        </button>
      </div>
    </div>
  );
}
